import type { Dispatch } from "react";
import { useState } from "react";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { newId, type EditorAction } from "../editor/state";
import type { DraftPlan, ZoneKind } from "../editor/types";
import type { ZoneReducedRejection } from "../floorApi";

/**
 * Zone management (Implementation plan §4): create/rename/reorder zones and switch a zone between
 * SEATED_TABLES and STANDING_HEADCOUNT(capacity, legalCapacity?) — the bar/standing surface (R4).
 * A standing-capacity reduction the server refuses (FL-4, party currently committed) comes back as a
 * typed rejection and is surfaced HERE on the offending zone, with the draft value preserved (the save
 * is not applied) — the R4 authoring-surface edge case.
 */
export interface ZonePanelProps {
  plan: DraftPlan;
  activeZoneId: string | null;
  disabled: boolean;
  dispatch: Dispatch<EditorAction>;
  zoneRejection?: ZoneReducedRejection | null;
}

export function ZonePanel({
  plan,
  activeZoneId,
  disabled,
  dispatch,
  zoneRejection,
}: ZonePanelProps) {
  const { t } = useTranslation();
  const [newName, setNewName] = useState("");
  const [newKind, setNewKind] = useState<ZoneKind>("SEATED_TABLES");

  function addZone() {
    const name = newName.trim();
    if (!name) return;
    dispatch({
      type: "ADD_ZONE",
      zone: {
        id: newId(),
        name,
        kind: newKind,
        capacity: newKind === "STANDING_HEADCOUNT" ? 20 : null,
        legalCapacity: null,
      },
    });
    setNewName("");
    setNewKind("SEATED_TABLES");
  }

  return (
    <section
      aria-label={t("floorPlan.zones.title")}
      className={css({ display: "flex", flexDirection: "column", gap: "3" })}
    >
      <h3 className={css({ fontSize: "md", fontWeight: "semibold" })}>
        {t("floorPlan.zones.title")}
      </h3>

      <ul
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "2",
          listStyle: "none",
          p: "0",
        })}
      >
        {plan.zones.map((z, i) => {
          const active = z.id === activeZoneId;
          const rejected = zoneRejection?.zoneId === z.id ? zoneRejection : null;
          return (
            <li
              key={z.id}
              data-active={active ? "true" : undefined}
              className={css({
                display: "flex",
                flexDirection: "column",
                gap: "2",
                p: "2",
                borderWidth: "1px",
                rounded: "md",
                borderColor: rejected
                  ? "border.error"
                  : active
                    ? "colorPalette.default"
                    : "border.default",
              })}
            >
              <div
                className={css({
                  display: "flex",
                  gap: "2",
                  alignItems: "center",
                  flexWrap: "wrap",
                })}
              >
                <button
                  type="button"
                  onClick={() => dispatch({ type: "SET_ACTIVE_ZONE", zoneId: z.id })}
                  aria-pressed={active}
                  className={css({
                    fontSize: "sm",
                    fontWeight: active ? "semibold" : "normal",
                    textAlign: "left",
                  })}
                >
                  {z.name || t("floorPlan.zones.untitled")}
                </button>
                <span className={css({ ml: "auto", display: "flex", gap: "1" })}>
                  <button
                    type="button"
                    disabled={disabled || i === 0}
                    aria-label={t("floorPlan.zones.moveUp")}
                    onClick={() => dispatch({ type: "MOVE_ZONE", id: z.id, dir: -1 })}
                    className={iconBtn}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    disabled={disabled || i === plan.zones.length - 1}
                    aria-label={t("floorPlan.zones.moveDown")}
                    onClick={() => dispatch({ type: "MOVE_ZONE", id: z.id, dir: 1 })}
                    className={iconBtn}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    disabled={disabled}
                    aria-label={t("floorPlan.zones.remove")}
                    onClick={() => dispatch({ type: "REMOVE_ZONE", id: z.id })}
                    className={css({ ...iconBtnBase, color: "fg.error" })}
                  >
                    ✕
                  </button>
                </span>
              </div>

              <label className={labelStyle}>
                {t("floorPlan.zones.name")}
                <input
                  className={inputStyle}
                  disabled={disabled}
                  value={z.name}
                  onChange={(e) =>
                    dispatch({ type: "RENAME_ZONE", id: z.id, name: e.target.value })
                  }
                />
              </label>

              <label className={labelStyle}>
                {t("floorPlan.zones.kind")}
                <select
                  className={inputStyle}
                  disabled={disabled}
                  value={z.kind}
                  onChange={(e) =>
                    dispatch({ type: "SET_ZONE_KIND", id: z.id, kind: e.target.value as ZoneKind })
                  }
                >
                  <option value="SEATED_TABLES">{t("floorPlan.zones.seated")}</option>
                  <option value="STANDING_HEADCOUNT">{t("floorPlan.zones.standing")}</option>
                </select>
              </label>

              {z.kind === "STANDING_HEADCOUNT" ? (
                <div className={css({ display: "flex", gap: "2" })}>
                  <label className={labelStyle}>
                    {t("floorPlan.zones.capacity")}
                    <input
                      type="number"
                      min={0}
                      className={inputStyle}
                      disabled={disabled}
                      value={z.capacity ?? 0}
                      onChange={(e) =>
                        dispatch({
                          type: "SET_ZONE_CAPACITY",
                          id: z.id,
                          capacity: Number(e.target.value) || 0,
                          legalCapacity: z.legalCapacity,
                        })
                      }
                    />
                  </label>
                  <label className={labelStyle}>
                    {t("floorPlan.zones.legalCapacity")}
                    <input
                      type="number"
                      min={0}
                      className={inputStyle}
                      disabled={disabled}
                      value={z.legalCapacity ?? ""}
                      onChange={(e) =>
                        dispatch({
                          type: "SET_ZONE_CAPACITY",
                          id: z.id,
                          capacity: z.capacity,
                          legalCapacity:
                            e.target.value.trim() === "" ? null : Number(e.target.value),
                        })
                      }
                    />
                  </label>
                </div>
              ) : null}

              {rejected ? (
                <p role="alert" className={css({ color: "fg.error", fontSize: "xs" })}>
                  {t("floorPlan.zones.reducedBelowCommitted", {
                    committed: rejected.committed,
                    requested: rejected.requestedCapacity,
                  })}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>

      <div className={css({ display: "flex", gap: "2", alignItems: "flex-end", flexWrap: "wrap" })}>
        <label className={labelStyle}>
          {t("floorPlan.zones.newName")}
          <input
            className={inputStyle}
            disabled={disabled}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </label>
        <select
          aria-label={t("floorPlan.zones.kind")}
          className={inputStyle}
          disabled={disabled}
          value={newKind}
          onChange={(e) => setNewKind(e.target.value as ZoneKind)}
        >
          <option value="SEATED_TABLES">{t("floorPlan.zones.seated")}</option>
          <option value="STANDING_HEADCOUNT">{t("floorPlan.zones.standing")}</option>
        </select>
        <button
          type="button"
          disabled={disabled || newName.trim() === ""}
          onClick={addZone}
          className={css({ px: "3", py: "2", rounded: "md", borderWidth: "1px", fontSize: "sm" })}
        >
          {t("floorPlan.zones.add")}
        </button>
      </div>
    </section>
  );
}

const labelStyle = css({
  display: "flex",
  flexDirection: "column",
  gap: "1",
  fontSize: "sm",
  flex: "1",
});
const inputStyle = css({ px: "2", py: "1", borderWidth: "1px", rounded: "md", w: "full" });
const iconBtnBase = {
  px: "2",
  py: "1",
  rounded: "md",
  borderWidth: "1px",
  fontSize: "sm",
} as const;
const iconBtn = css(iconBtnBase);
